import { observer } from 'mobx-react';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider } from '@wix/design-system';
import { Check } from '@wix/wix-ui-icons-common';
import { dataHooks } from '../../../dataHooks';
import { AppContextProvider } from '../../AppLoader';
import { ButtonWithLoader } from '../../ButtonWithLoader/ButtonWithLoader';
import { ThemedText, ThemedTextButton } from '../../ThemedComponents';
import { DealerOfferCard, NotNowLink } from './DealerOfferCard';

interface DetailRowProps {
  label: string;
  value?: string;
  isVerified: boolean;
  onEdit: () => void;
  editDataHook?: string;
}

const DetailRow: React.FC<DetailRowProps> = observer(
  ({ label, value, isVerified, onEdit, editDataHook }) => {
    const { t } = useTranslation();
    return (
      <Box direction="vertical" width="100%" gap={1}>
        <ThemedText size="small" secondary>
          {label}
        </ThemedText>
        <Box direction="horizontal" align="space-between" width="100%">
          <ThemedText weight="bold" dir="ltr">
            {value}
          </ThemedText>
          {isVerified ? (
            <Box gap={1} verticalAlign="middle">
              <Check style={{ color: '#25A55A' }} size="18px" />
              <ThemedText size="small" skin="success">
                {t('post.login.verified')}
              </ThemedText>
            </Box>
          ) : (
            <ThemedTextButton
              dataHook={editDataHook}
              as="a"
              underline="always"
              onClick={onEdit}
              skin="standard"
              size="small"
            >
              {t('post.login.confirm.details.edit')}
            </ThemedTextButton>
          )}
        </Box>
      </Box>
    );
  },
);

/**
 * redesigned "confirm your account details" Dealer offer (wix-2025): the account
 * email and recovery phone, each Verified or editable.
 */
export const ConfirmDetailsRedesign: React.FC = observer(() => {
  const {
    rootStore: {
      confirmDetailsStore: store,
      phoneStore,
      postLoginStore,
      displayStore: { preset },
    },
  } = useContext(AppContextProvider);
  const { t } = useTranslation();
  const internationalNumber =
    phoneStore.newInternationalNumber ?? phoneStore.phone?.internationalNumber;
  const isAllVerified = store.isEmailVerified && store.isRecoveryPhoneVerified;

  return (
    <DealerOfferCard
      title={t('post.login.confirm.details.title')}
      subtitle={t('post.login.confirm.details.subtitle')}
      primaryButton={
        <ButtonWithLoader
          skin={preset.buttonDesign?.skin ?? 'standard'}
          onClick={store.confirm}
        >
          {isAllVerified ? t('post.login.done') : t('post.login.confirm')}
        </ButtonWithLoader>
      }
      skip={<NotNowLink onClick={store.showLater} />}
    >
      <Box direction="vertical" width="100%" gap={3}>
        <DetailRow
          label={t('post.login.confirm.details.email')}
          value={postLoginStore.userDetails?.email}
          isVerified={store.isEmailVerified}
          onEdit={store.editEmail}
          editDataHook={dataHooks.postLogin.confirmDetails.editEmailButton}
        />
        <Divider />
        <DetailRow
          label={t('post.login.confirm.details.recovery.phone')}
          value={internationalNumber}
          isVerified={store.isRecoveryPhoneVerified}
          onEdit={store.editPhone}
        />
      </Box>
    </DealerOfferCard>
  );
});
